import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from 'react-redux'
import { useStripe, useElements, CardNumberElement, CardCvcElement, CardExpiryElement } from "@stripe/react-stripe-js"
import { getIntentForStage, getIntentForInvite, cancelIntent } from '../../actions/payment'
import op from '../../util/objectPath'
import Dialog from './../dialog'
import './styles.css'

const useOptions = () => {
  return useMemo(
    () => ({
      style: {
        base: {
          color: "#424770",
          letterSpacing: "0.025em",
          "::placeholder": { color: "#aab7c4" }
        },
        invalid: {
          color: "#9e2146"
        }
      }
    }),
    []
  )
}

const SplitForm = ({ clientSecret, onSuccess, onFailed, onCanceled }) => {
  const stripe = useStripe()
  const elements = useElements()
  const options = useOptions()
  const [paying, setPaying] = useState(false)

  const handleSubmit = async event => {
    event.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      console.warn('Stripe not yet fully loaded.')
      return
    }

    setPaying(true)

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement)
      }
    })

    if (result.error) {
      // Show error to your customer (e.g., insufficient funds)
      onFailed(result.error.message)
    } else {
      // The payment has been processed!
      if (result.paymentIntent.status === 'succeeded') {
        onSuccess()
      }
    }
  }

  return (
    <div className="stripe">
      <form onSubmit={handleSubmit}>
        <label>
          Card number
          <CardNumberElement options={options} />
        </label>
        <label>
          Expiration date
          <CardExpiryElement options={options} />
        </label>
        <label>
          CVC
          <CardCvcElement options={options} />
        </label>
        <div className="buttons" role="group">
          <button type="submit" disabled={!stripe || paying} className="btn btn-primary">Pay</button>
          <button type="button" className="btn btn-secondary" onClick={() => onCanceled()}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

const PayButton = ({ stageId, inviteId, onStarted, onEnded, onCancel }) => {
  const dispatch = useDispatch()
  const [paymentState, setPaymentState] = useState('button')
  const [failedMessage, setFailedMessage] = useState('')

  const clientSecret = useSelector(state => op(state, `payment.intents[${stageId || inviteId}].clientSecret`))
  const intentId = useSelector(state => op(state, `payment.intents[${stageId || inviteId}].intentId`))

  const onPayClick = async (evt) => {
    evt.preventDefault()
    onStarted()
    setPaymentState('intent')
    if (stageId != null) {
      await getIntentForStage('Team for stage ' + stageId, stageId)(dispatch)
    } else if (inviteId != null) {
      await getIntentForInvite(inviteId)(dispatch)
    }
    setPaymentState('form')
  }

  const onPaymentSuccessful = () => {
    setPaymentState('successful')
    onEnded()
  }

  const onPaymentFailed = (reason) => {
    setPaymentState('failed')
    setFailedMessage(reason)
  }

  const onPaymentFailedAck = () => {
    setPaymentState('button')
    onCancel()
  }

  const onPaymentCanceled = async () => {
    setPaymentState('canceled')
    await cancelIntent(intentId)(dispatch)
    setPaymentState('button')
    onCancel()
  }

  if (paymentState === 'button') {
    return <button className="btn btn-primary" onClick={onPayClick}>Pay with credit card</button>
  } else {
    let content = null
    if (paymentState === 'form') {
      content = <SplitForm 
        clientSecret={clientSecret}
        onSuccess={onPaymentSuccessful} 
        onFailed={onPaymentFailed} 
        onCanceled={onPaymentCanceled} />
    } else if (paymentState === 'intent') {
      content = 'Preparing secure payment...'
    } else if (paymentState === 'successful') {
      content = 'Payment successful, finishing up.'
    } else if (paymentState === 'failed') {
      content = (
        <div>
          <div>
            Payment failed: {failedMessage}
          </div>
          <div className="mt-3">
            <button type="button" className="btn btn-primary" onClick={onPaymentFailedAck}>Ok</button>
          </div>
        </div>)
    } else if (paymentState === 'canceled') {
      content = `Canceling payment...`
    } else {
      return null
    }

    return (<Dialog title='Pay with card' >{content}</Dialog>  )
  }
}

export default PayButton;